<template>
  <AppSheet mode="centered" :is-open="!!release" :backdrop-dismiss="true" @update:is-open="onUpdateOpen">
    <div v-if="release" class="whats-new-content flex max-h-[85vh] flex-col pb-6 pt-6" data-testid="whats-new">
      <p class="px-6 text-center text-[12px] font-extrabold uppercase tracking-[0.1em] text-brand-deep">
        {{ t('whatsNew.title', { version: shortLabel }) }}
      </p>

      <!-- Une nouveauté par page : le défilement natif à aimantation (le même
           que WeeklyRecapCarousel), au doigt ou par « Suivant ». -->
      <div ref="railEl" class="no-scrollbar mt-4 flex min-h-0 flex-1 snap-x snap-mandatory overflow-x-auto overflow-y-hidden"
        data-testid="whats-new-rail" @scroll.passive="onScroll">
        <section v-for="(item, i) in release.items" :key="item.key"
          class="flex w-full shrink-0 snap-center flex-col overflow-y-auto px-6"
          :aria-label="t('whatsNew.slideLabel', { n: i + 1, total: release.items.length })"
          :data-testid="`whats-new-${item.key}`">
          <!-- La capture d'écran de la nouveauté quand elle existe ; sinon son
               icône, en grand, sur un aplat. -->
          <div class="flex h-[min(38vh,260px)] shrink-0 items-center justify-center overflow-hidden rounded-[22px]"
            :class="{ 'bg-warm-2': !imageFor(item) }">
            <img v-if="imageFor(item)" :src="imageFor(item)!" alt="" class="max-h-full max-w-full rounded-[18px] object-contain"
              draggable="false" />
            <IconTile v-else :icon="item.icon" :size="104" :icon-size="52" tone="joy" />
          </div>
          <h2 class="mt-5 font-display text-[22px] font-bold leading-tight text-ink text-balance">
            {{ t(`whatsNew.${release.id}.${item.key}.title`) }}
          </h2>
          <p class="mt-2 text-[15px] leading-relaxed text-ink-2">
            {{ t(`whatsNew.${release.id}.${item.key}.body`) }}
          </p>
        </section>
      </div>

      <!-- Où l'on en est : un point par page, celui en cours allongé. -->
      <div v-if="release.items.length > 1" class="mt-5 flex justify-center gap-1.5" aria-hidden="true">
        <span v-for="(item, i) in release.items" :key="item.key"
          class="h-2 rounded-full transition-all duration-300"
          :class="i === index ? 'w-5 bg-brand-deep' : 'w-2 bg-line'" />
      </div>

      <div class="mt-5 shrink-0 px-6">
        <Button v-if="!isLast" class="h-12 w-full" data-testid="whats-new-next" @click="next">
          {{ t('whatsNew.next') }}
        </Button>
        <Button v-else class="h-12 w-full" data-testid="whats-new-close" @click="close">
          {{ t('whatsNew.close') }}
        </Button>
      </div>
    </div>
  </AppSheet>
</template>

<script setup lang="ts">
/**
 * « Les nouveautés de la version 3.1 », une fois par version et par appareil,
 * au retour sur l'Accueil après une mise à jour (#408). Qui la voit et quand :
 * lib/whatsNew.
 *
 * Une nouveauté par page, pour laisser la place au texte et à une capture
 * d'écran dans la langue du parent (lib/whatsNew `whatsNewImage` ; l'icône en
 * grand sans elle). On passe
 * d'une page à l'autre au doigt ou par « Suivant » ; la dernière ferme.
 *
 * Elle attend son tour : l'Accueil seulement (jamais le mode enfant, jamais
 * l'onboarding), un instant après l'arrivée, et après une célébration de la
 * file (récompense, coffre, bravo…) si une est à l'écran. Vue dès qu'elle
 * s'ouvre : quitter l'app au milieu ne la fait pas revenir.
 *
 * Les Réglages la rouvrent à la demande (`app:open-whats-new`) : celle de la
 * version installée, déjà vue ou non, sur l'écran où l'on est.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppSheet from '@/components/common/AppSheet.vue'
import IconTile from '@/components/common/IconTile.vue'
import { Button } from '@/components/ui/button'
import { HOME_PATH } from '@/router/paths'
import { useAppContextStore } from '@/stores/appContext'
import { useAuthStore } from '@/stores/auth'
import { useNotificationQueue } from '@/composables/useNotificationQueue'
import {
  latestWhatsNew, markWhatsNewSeen, pickWhatsNew, readWhatsNewSeen, whatsNewImage,
  type WhatsNewItem, type WhatsNewRelease,
} from '@/lib/whatsNew'
import { eventBus } from '@/utils/eventBus'

const { t, locale } = useI18n({ useScope: 'global' })
const route = useRoute()
const appContext = useAppContextStore()
const authStore = useAuthStore()
const { currentNotification } = useNotificationQueue()

// Lue à chaque contrôle (et pas une fois au chargement) : les tests la fixent.
const currentVersion = () => import.meta.env.VITE_APP_VERSION || '0.0.0'
const SETTLE_MS = 1200

const release = ref<WhatsNewRelease | null>(null)
const shortLabel = computed(() => release.value?.version.split('.').slice(0, 2).join('.') ?? '')

// ── Les pages ────────────────────────────────────────────────────────
const railEl = ref<HTMLElement | null>(null)
const index = ref(0)
const isLast = computed(() => !release.value || index.value >= release.value.items.length - 1)
const imageFor = (item: WhatsNewItem) => (release.value ? whatsNewImage(release.value, item, locale.value) : null)

// Pendant le défilement lancé par « Suivant », la page visée est déjà connue :
// relire scrollLeft à mi-course ramènerait `index` en arrière, et un second
// tap rapide repartirait de l'ancienne page.
let programmaticUntil = 0

function onScroll() {
  const el = railEl.value
  if (!el || !el.clientWidth || Date.now() < programmaticUntil) return
  const count = release.value?.items.length ?? 0
  index.value = Math.min(count - 1, Math.max(0, Math.round(el.scrollLeft / el.clientWidth)))
}

function next() {
  if (!release.value) return
  index.value = Math.min(release.value.items.length - 1, index.value + 1)
  programmaticUntil = Date.now() + 600
  const el = railEl.value
  const reduce = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  el?.scrollTo?.({ left: index.value * el.clientWidth, behavior: reduce ? 'auto' : 'smooth' })
}

// ── L'ouverture ──────────────────────────────────────────────────────
let settleTimer: ReturnType<typeof setTimeout> | undefined
let stopQueueWatch: (() => void) | null = null

function show(entry: WhatsNewRelease) {
  markWhatsNewSeen(currentVersion())
  index.value = 0
  release.value = entry
  void nextTick(() => railEl.value?.scrollTo?.({ left: 0 }))
}

const stopOpenRequest = eventBus.on('app:open-whats-new', () => {
  const latest = latestWhatsNew(currentVersion())
  if (latest && !release.value) show(latest)
})
onBeforeUnmount(() => {
  clearTimeout(settleTimer)
  stopQueueWatch?.()
  stopOpenRequest()
})

function candidate(): WhatsNewRelease | null {
  const household = appContext.currentHousehold
  if (!household) return null
  // Les comptes de captures du store ne doivent pas voir la modale passer.
  if (authStore.user?.email?.startsWith('screenshot-')) return null
  return pickWhatsNew({
    current: currentVersion(),
    lastSeen: readWhatsNewSeen(),
    householdCreatedOn: household.created_on_app_version,
  })
}

function openOnHome(entry: WhatsNewRelease) {
  if (route.path !== HOME_PATH || release.value) return
  show(entry)
}

watch(
  () => [route.path, appContext.currentHousehold?.id] as const,
  ([path]) => {
    clearTimeout(settleTimer)
    if (path !== HOME_PATH || release.value || !candidate()) return
    settleTimer = setTimeout(() => {
      const entry = candidate()
      if (!entry) return
      if (!currentNotification.value) {
        openOnHome(entry)
        return
      }
      stopQueueWatch?.()
      stopQueueWatch = watch(currentNotification, (notif) => {
        if (notif) return
        stopQueueWatch?.()
        stopQueueWatch = null
        openOnHome(entry)
      })
    }, SETTLE_MS)
  },
  { immediate: true },
)

function close() {
  release.value = null
}

function onUpdateOpen(isOpen: boolean) {
  if (!isOpen) close()
}
</script>
